import { getPublicStudioCoordinates } from './locationPrivacy';

export const SEARCH_LOCATION_MODES = {
  NONE: 'none',
  FLEXIBLE: 'flexible',
  COUNTRY: 'country',
  PLACE: 'place',
};

export const SEARCH_FETCH_GUARD_REASONS = {
  SKIPPED: 'skipped',
  WAITING_FOR_LOCATION: 'waiting_for_location',
  UNRESOLVABLE_LOCATION: 'unresolvable_location',
};

const EARTH_RADIUS_KM = 6371;
const KNOWN_SEARCH_CENTERS = {
  'köln': { latitude: 50.9375, longitude: 6.9603 },
  'köln, germany': { latitude: 50.9375, longitude: 6.9603 },
  'köln, deutschland': { latitude: 50.9375, longitude: 6.9603 },
  'cologne': { latitude: 50.9375, longitude: 6.9603 },
  'cologne, germany': { latitude: 50.9375, longitude: 6.9603 },
  'cologne, deutschland': { latitude: 50.9375, longitude: 6.9603 },
  'düsseldorf': { latitude: 51.2277, longitude: 6.7735 },
  'düsseldorf, germany': { latitude: 51.2277, longitude: 6.7735 },
  'düsseldorf, deutschland': { latitude: 51.2277, longitude: 6.7735 },
  'dusseldorf': { latitude: 51.2277, longitude: 6.7735 },
  'dusseldorf, germany': { latitude: 51.2277, longitude: 6.7735 },
  'dusseldorf, deutschland': { latitude: 51.2277, longitude: 6.7735 },
  'duesseldorf': { latitude: 51.2277, longitude: 6.7735 },
  'duesseldorf, germany': { latitude: 51.2277, longitude: 6.7735 },
  'duesseldorf, deutschland': { latitude: 51.2277, longitude: 6.7735 },
};

const normalizeText = (value) =>
  typeof value === 'string'
    ? value.trim().toLowerCase().replace(/\s+/g, ' ')
    : '';

export const normalizeSearchLocation = ({ location, locationPlaceId } = {}) => {
  const label = typeof location === 'string' ? location.trim() : '';
  const normalized = normalizeText(label);
  const placeId = typeof locationPlaceId === 'string' ? locationPlaceId.trim() : '';

  if (!normalized) {
    return { mode: SEARCH_LOCATION_MODES.NONE, label: '', placeId: '' };
  }

  if (normalized === 'ich bin flexibel') {
    return { mode: SEARCH_LOCATION_MODES.FLEXIBLE, label, placeId: '' };
  }

  if (normalized === 'deutschland' || normalized === 'germany') {
    return { mode: SEARCH_LOCATION_MODES.COUNTRY, label, placeId: '' };
  }

  return {
    mode: SEARCH_LOCATION_MODES.PLACE,
    label,
    placeId,
    center: KNOWN_SEARCH_CENTERS[normalized] || null,
  };
};

export const getSearchFetchGuard = ({
  skip = false,
  isResolvingLocation = false,
  isPlaceSearch = false,
  selectedLocation = null,
  locationResolveFailed = false,
} = {}) => {
  if (skip) return SEARCH_FETCH_GUARD_REASONS.SKIPPED;

  if (isPlaceSearch && !selectedLocation && locationResolveFailed) {
    return SEARCH_FETCH_GUARD_REASONS.UNRESOLVABLE_LOCATION;
  }

  if (isResolvingLocation || (isPlaceSearch && !selectedLocation)) {
    return SEARCH_FETCH_GUARD_REASONS.WAITING_FOR_LOCATION;
  }

  return null;
};

export const calculateDistanceKm = (origin, destination) => {
  const originLat = Number(origin?.latitude ?? origin?.lat);
  const originLng = Number(origin?.longitude ?? origin?.lng);
  const destinationLat = Number(destination?.latitude ?? destination?.lat);
  const destinationLng = Number(destination?.longitude ?? destination?.lng);

  if (
    !Number.isFinite(originLat) ||
    !Number.isFinite(originLng) ||
    !Number.isFinite(destinationLat) ||
    !Number.isFinite(destinationLng)
  ) {
    return null;
  }

  const dLat = (destinationLat - originLat) * Math.PI / 180;
  const dLng = (destinationLng - originLng) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(originLat * Math.PI / 180) *
      Math.cos(destinationLat * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
};

export const buildSearchBounds = ({ latitude, longitude, radiusKm = 30 } = {}) => {
  const centerLatitude = Number(latitude);
  const centerLongitude = Number(longitude);
  const radius = Number(radiusKm);

  if (
    !Number.isFinite(centerLatitude) ||
    !Number.isFinite(centerLongitude) ||
    !Number.isFinite(radius) ||
    radius <= 0
  ) {
    return null;
  }

  const latDegreePerKm = 1 / 110.574;
  const lonDegreePerKm = 1 / (111.32 * Math.cos(centerLatitude * Math.PI / 180));
  const latDelta = radius * latDegreePerKm;
  const lonDelta = radius * lonDegreePerKm;

  return {
    minLat: centerLatitude - latDelta,
    maxLat: centerLatitude + latDelta,
    minLon: centerLongitude - lonDelta,
    maxLon: centerLongitude + lonDelta,
    centerLatitude,
    centerLongitude,
    radiusKm: radius,
  };
};

const studioSortValue = (studio, field) => {
  const value = studio?.[field];
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
};

export const filterAndSortStudiosByDistance = (studios = [], bounds) => {
  if (!bounds) return Array.isArray(studios) ? [...studios] : [];

  const center = {
    latitude: bounds.centerLatitude,
    longitude: bounds.centerLongitude,
  };

  return (Array.isArray(studios) ? studios : [])
    .reduce((result, studio) => {
      const coords = getPublicStudioCoordinates(studio);
      if (!coords) return result;

      const insideBox =
        coords.latitude >= bounds.minLat &&
        coords.latitude <= bounds.maxLat &&
        coords.longitude >= bounds.minLon &&
        coords.longitude <= bounds.maxLon;

      if (!insideBox) return result;

      const distanceKm = calculateDistanceKm(center, coords);
      if (distanceKm == null || distanceKm > bounds.radiusKm) return result;

      result.push({
        ...studio,
        searchDistanceKm: distanceKm,
      });

      return result;
    }, [])
    .sort((a, b) => {
      const distanceDiff = a.searchDistanceKm - b.searchDistanceKm;
      if (distanceDiff !== 0) return distanceDiff;

      const nameDiff = studioSortValue(a, 'StudioName').localeCompare(
        studioSortValue(b, 'StudioName')
      );
      if (nameDiff !== 0) return nameDiff;

      return studioSortValue(a, 'id').localeCompare(studioSortValue(b, 'id'));
    });
};

export const dedupeStudiosById = (studios = []) => {
  const seen = new Set();
  const deduped = [];

  (Array.isArray(studios) ? studios : []).forEach((studio) => {
    if (!studio?.id) {
      deduped.push(studio);
      return;
    }

    if (seen.has(studio.id)) return;
    seen.add(studio.id);
    deduped.push(studio);
  });

  return deduped;
};

export const calculateMapCenter = (
  studios = [],
  searchCenter = null,
  { includeSearchCenterInBounds = false } = {}
) => {
  const validLocations = (Array.isArray(studios) ? studios : [])
    .map((studio) => getPublicStudioCoordinates(studio))
    .filter(Boolean);
  const normalizedSearchCenter = searchCenter
    ? {
        latitude: Number(searchCenter.latitude ?? searchCenter.lat),
        longitude: Number(searchCenter.longitude ?? searchCenter.lng),
      }
    : null;
  const hasSearchCenter =
    normalizedSearchCenter &&
    Number.isFinite(normalizedSearchCenter.latitude) &&
    Number.isFinite(normalizedSearchCenter.longitude);

  if (validLocations.length === 0) {
    return hasSearchCenter
      ? { lat: normalizedSearchCenter.latitude, lng: normalizedSearchCenter.longitude }
      : null;
  }

  const locationsForBounds = includeSearchCenterInBounds && hasSearchCenter
    ? [...validLocations, normalizedSearchCenter]
    : validLocations;

  if (locationsForBounds.length === 1) {
    return {
      lat: locationsForBounds[0].latitude,
      lng: locationsForBounds[0].longitude,
    };
  }

  const bounds = locationsForBounds.reduce(
    (acc, coords) => ({
      minLat: Math.min(acc.minLat, coords.latitude),
      maxLat: Math.max(acc.maxLat, coords.latitude),
      minLng: Math.min(acc.minLng, coords.longitude),
      maxLng: Math.max(acc.maxLng, coords.longitude),
    }),
    {
      minLat: locationsForBounds[0].latitude,
      maxLat: locationsForBounds[0].latitude,
      minLng: locationsForBounds[0].longitude,
      maxLng: locationsForBounds[0].longitude,
    }
  );

  return {
    lat: (bounds.minLat + bounds.maxLat) / 2,
    lng: (bounds.minLng + bounds.maxLng) / 2,
  };
};

export const calculateMapBounds = (
  studios = [],
  searchCenter = null,
  { includeSearchCenterInBounds = false } = {}
) => {
  const validLocations = (Array.isArray(studios) ? studios : [])
    .map((studio) => getPublicStudioCoordinates(studio))
    .filter(Boolean);

  if (validLocations.length === 0) {
    return null;
  }

  const normalizedSearchCenter = searchCenter
    ? {
        latitude: Number(searchCenter.latitude ?? searchCenter.lat),
        longitude: Number(searchCenter.longitude ?? searchCenter.lng),
      }
    : null;
  const hasSearchCenter =
    normalizedSearchCenter &&
    Number.isFinite(normalizedSearchCenter.latitude) &&
    Number.isFinite(normalizedSearchCenter.longitude);

  const locationsForBounds = includeSearchCenterInBounds && hasSearchCenter
    ? [...validLocations, normalizedSearchCenter]
    : validLocations;

  const bounds = locationsForBounds.reduce(
    (acc, coords) => ({
      south: Math.min(acc.south, coords.latitude),
      north: Math.max(acc.north, coords.latitude),
      west: Math.min(acc.west, coords.longitude),
      east: Math.max(acc.east, coords.longitude),
    }),
    {
      south: locationsForBounds[0].latitude,
      north: locationsForBounds[0].latitude,
      west: locationsForBounds[0].longitude,
      east: locationsForBounds[0].longitude,
    }
  );

  return bounds;
};
